import React from 'react';
import { Container, Typography, Box, Stack, Button, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';

const FooterContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.grey[900],
  color: 'white',
  padding: theme.spacing(8, 0, 4, 0)
}));

const SocialButton = styled(Button)(() => ({
  minWidth: 48,
  width: 48,
  height: 48,
  borderRadius: '50%',
  color: 'rgba(255, 255, 255, 0.7)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  '&:hover': {
    color: 'white',
    borderColor: 'rgba(255, 255, 255, 0.5)',
    backgroundColor: 'rgba(255, 255, 255, 0.1)'
  }
}));

const Footer: React.FC = () => {
  return (
    <FooterContainer>
      <Container maxWidth="lg">
        <Stack spacing={6}>
          {/* Main Footer Content */}
          <Stack 
            direction={{ xs: 'column', md: 'row' }} 
            spacing={6} 
            justifyContent="space-between"
            alignItems={{ xs: 'center', md: 'flex-start' }}
          >
            {/* Brand Section */}
            <Box textAlign={{ xs: 'center', md: 'left' }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 2,
                  background: 'linear-gradient(45deg, #FFD700, #FFA500)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                RiteReview
              </Typography>
              
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 3, maxWidth: 300 }}>
                Transform your customer feedback into powerful social proof. 
                Launching Q4 2025.
              </Typography>
              
              <Stack direction="row" spacing={2} justifyContent={{ xs: 'center', md: 'flex-start' }}>
                <SocialButton>
                  <TwitterIcon />
                </SocialButton>
                <SocialButton>
                  <LinkedInIcon />
                </SocialButton>
                <SocialButton>
                  <FacebookIcon />
                </SocialButton>
                <SocialButton>
                  <EmailIcon />
                </SocialButton>
              </Stack>
            </Box>

            {/* Quick Links */}
            <Stack spacing={4} direction={{ xs: 'column', sm: 'row' }} textAlign={{ xs: 'center', md: 'left' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                  Product
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                    Features
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                    How it Works
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                    Pricing
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                    FAQ
                  </Typography>
                </Stack>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                  Company
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                    About Us
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                    Contact
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                    Privacy Policy
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                    Terms of Service
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            {/* CTA Section */}
            <Box textAlign={{ xs: 'center', md: 'right' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                Ready to get started?
              </Typography>
              
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 3 }}>
                Join the waitlist and be the first to know when we launch.
              </Typography>
              
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#064E3B',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  borderRadius: '50px',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#047857',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Join Waitlist
              </Button>
            </Box>
          </Stack>

          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* Bottom Footer */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            justifyContent="space-between" 
            alignItems="center"
            spacing={2}
          >
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              © 2024 RiteReview. All rights reserved.
            </Typography>
            
            <Stack direction="row" spacing={3}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                Privacy
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                Terms
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', cursor: 'pointer', '&:hover': { color: 'white' } }}>
                Support
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </FooterContainer>
  );
};

export default Footer;